import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { useToast } from "@dash/toast";
import Decimal from "decimal.js";
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import { Region } from "@megaron/crm-contracts";
import { useClientManager } from "@megaron/react-clients";

import { ensureGoogleMapsLoaded } from "./googleMapsLoader";
import { LocationPicker, RegionOwnerAddress } from "./LocationPicker";

type Props = {
  onClose: () => void;
  region: Region;
  address: RegionOwnerAddress;
  regionQueryKey: string | string[];
};

export const EditRegionOwnerAddressDialog: React.FC<Props> = ({ onClose, region, address, regionQueryKey }) => {
  const { isMobile } = useDeviceType();

  const [newAddress, setNewAddress] = useState<RegionOwnerAddress>(address);

  const toast = useToast();

  const { mutate, isLoading } = useClientManager("crm").saveRegion().useMutation();

  const queryClient = useQueryClient();

  const handleUpdateAddress = useCallback(
    (address: RegionOwnerAddress) => {
      if (!address.latitude || !address.longitude) {
        toast.error("Wybierz adres poprzez wyszukiwarkę.");
        return;
      }

      if (!address.city && !address.street && !address.country && !address.postalCode) {
        toast.error("Uzupełnij wszystkie pola.");
        return;
      }

      mutate(
        {
          ...region,
          addressCity: address.city,
          addressStreet: address.street,
          addressCountry: address.country,
          addressPostalCode: address.postalCode,
          homeLatitude: new Decimal(address.latitude),
          homeLongitude: new Decimal(address.longitude),
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries(regionQueryKey);
            toast.info("Adres został zaktualizowany.");
            onClose();
          },
          onError: () => {
            toast.error("Wystąpił błąd podczas aktualizacji adresu.");
          },
        },
      );
    },
    [onClose, region, toast, mutate, queryClient, regionQueryKey],
  );

  useEffect(() => {
    ensureGoogleMapsLoaded();
  }, []);

  return (
    <Dialog
      onClose={onClose}
      placement={isMobile ? "top" : "center"}
      header={"Zmień adres"}
      css={{ minWidth: isMobile ? "unset" : "26rem" }}
    >
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "400px",
          margin: "auto",
          gap: "20px",
          width: "100%",
        }}
      >
        <LocationPicker onAddressChange={(addr) => setNewAddress({ ...address, ...addr })} address={newAddress} />
        <Button onClick={() => handleUpdateAddress(newAddress)} isLoading={isLoading} css={{ alignSelf: "flex-end" }}>
          Zatwierdź
        </Button>
      </div>
    </Dialog>
  );
};
